import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaLightbulb,
  FaUsers,
  FaGraduationCap,
  FaRocket,
  FaLaptopCode,
  FaHandshake,
  FaPaperclip,
  FaCheckCircle,
  FaTimes,
  FaFileAlt,
  FaPhoneAlt,
  FaComments,
  FaFileSignature,
  FaMapMarkerAlt,
  FaBriefcase,
} from "react-icons/fa";

import { db, storage, firebaseConfig } from "../../firebase";
import { collection, addDoc, serverTimestamp, getDocs, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, signOut } from "firebase/auth";

import "./CareerPage.css";
import careerImg from "./CareerImage.png";

// Phone-OTP verification runs on a SECOND Firebase app instance so that
// confirming the OTP (which technically signs the applicant in with their
// phone number) can never replace a customer's existing login session on
// the main app.
const getOtpAuth = () => {
  const name = "career-phone-verify";
  const app = getApps().find((a) => a.name === name) || initializeApp(firebaseConfig, name);
  return getAuth(app);
};

// "9876543210" -> "+919876543210"; anything already starting with + is
// kept as typed (spaces/dashes stripped).
const toE164 = (raw) => {
  const cleaned = raw.replace(/[\s-]/g, "");
  if (cleaned.startsWith("+")) return cleaned;
  if (/^\d{10}$/.test(cleaned)) return `+91${cleaned}`;
  return null;
};

// ── Data ───────────────────────────────────────────────────────────────────

const BENEFITS = [
  {
    icon: <FaRocket />,
    title: "Career Growth",
    desc: "Grow with exciting opportunities, challenging projects, and leadership exposure.",
  },
  {
    icon: <FaLightbulb />,
    title: "Innovation",
    desc: "Work on Embedded Systems, Web Applications, TFT Displays, and next-generation electronics."
  },
  {
    icon: <FaUsers />,
    title: "Collaborative Culture",
    desc: "Join a supportive team where ideas are valued and everyone contributes.",
  },
  {
    icon: <FaGraduationCap />,
    title: "Continuous Learning",
    desc: "Learn modern frameworks, embedded technologies, industry best practices, and real-world problem solving through mentorship and hands-on projects.",
  },
  {
    icon: <FaLaptopCode />,
    title: "Modern Technologies",
    desc: "Build innovative solutions using React, Flutter, Embedded Linux, Android, ARM processors, and modern display technologies.",
  },
  {
    icon: <FaHandshake />,
    title: "Integrity & Trust",
    desc: "Be part of a company that values transparency, ethics, and long-term relationships.",
  },
];

// Fallback positions for the application form's dropdown -- only used
// while no job postings exist in Firestore (`career_jobs`, managed from
// the ERP's BIBA Website > Careers page). Once real postings exist, the
// dropdown lists those instead.
const POSITIONS = [
  // Engineering — Hardware
  "Embedded Systems Engineer",
  "Firmware Engineer",
  "R&D Engineer",

  // Engineering — Software
  "Frontend Developer",
  "Full Stack Developer",

  // Manufacturing & Quality (EMS)
  "Quality Assurance Engineer",

  // Sales, Marketing & Business
  "Sales & Business Development",
  "Marketing Executive",
  "Technical Sales Support",

  // Support Functions
  "Customer Support Engineer",
  "HR Executive",
  "Finance & Accounts Executive",

  "Other",
];

const PROCESS_STEPS = [
  { icon: <FaFileAlt />, label: "Apply", desc: "Submit your details and resume." },
  { icon: <FaPhoneAlt />, label: "Screening", desc: "A quick call to understand your background." },
  { icon: <FaComments />, label: "Interview", desc: "Meet the team, discuss the role in depth." },
  { icon: <FaFileSignature />, label: "Offer", desc: "We extend an offer and welcome you aboard." },
];

const MAX_RESUME_MB = 5;
const ACCEPTED_RESUME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const initialFormState = { position: "", name: "", city: "", email: "", phone: "", message: "", website: "" };
// ── Inline application form ───────────────────────────────────────────────

const ApplicationForm = ({ positions, selectedPosition }) => {
  const [formData, setFormData] = useState(initialFormState);

  // An "Apply" click on one of the Open Positions cards preselects that
  // role here (the card scrolls down to this form).
  useEffect(() => {
    if (selectedPosition) {
      setFormData((prev) => ({ ...prev, position: selectedPosition }));
    }
  }, [selectedPosition]);
  const [resumeFile, setResumeFile] = useState(null);
  const [fieldError, setFieldError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const fileInputRef = useRef(null);

  // ── Phone OTP verification ──────────────────────────────────────────
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState("");
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const resetPhoneVerification = () => {
    setOtpSent(false);
    setOtpCode("");
    setPhoneVerified(false);
    setVerifiedPhone("");
    setOtpError("");
    confirmationRef.current = null;
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.clear();
      } catch {
        // Already cleared -- fine.
      }
      recaptchaRef.current = null;
    }
  };

  const handleSendOtp = async () => {
    setOtpError("");
    const e164 = toE164(formData.phone.trim());
    if (!e164) {
      setOtpError("Enter a valid 10-digit mobile number first.");
      return;
    }
    setOtpBusy(true);
    try {
      const otpAuth = getOtpAuth();
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(otpAuth, "cr-recaptcha", { size: "invisible" });
      }
      confirmationRef.current = await signInWithPhoneNumber(otpAuth, e164, recaptchaRef.current);
      setOtpSent(true);
    } catch (err) {
      console.error("OTP send failed:", err);
      setOtpError(
        err?.code === "auth/too-many-requests"
          ? "Too many attempts from this device. Please try again later."
          : "Could not send the OTP. Check the number and try again."
      );
      // A consumed/errored reCAPTCHA can't be reused -- rebuild next try.
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch {
          // Already cleared -- fine.
        }
        recaptchaRef.current = null;
      }
    } finally {
      setOtpBusy(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (!otpCode.trim() || !confirmationRef.current) return;
    setOtpBusy(true);
    setOtpError("");
    try {
      const result = await confirmationRef.current.confirm(otpCode.trim());
      setPhoneVerified(true);
      setVerifiedPhone(result.user.phoneNumber || toE164(formData.phone.trim()));
      // The OTP flow signs the applicant into the secondary app -- sign
      // straight back out; we only wanted the number confirmed.
      signOut(getOtpAuth()).catch(() => {});
    } catch (err) {
      console.error("OTP confirm failed:", err);
      setOtpError(
        err?.code === "auth/invalid-verification-code"
          ? "That OTP is incorrect. Please check and try again."
          : "Could not verify the OTP. Please try again."
      );
    } finally {
      setOtpBusy(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Changing the number after verifying invalidates the verification.
    if (name === "phone" && (phoneVerified || otpSent)) {
      resetPhoneVerification();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setResumeFile(null);
      return;
    }

    const fileExt = file.name.split(".").pop().toLowerCase();
    const hasValidExt = ["pdf", "doc", "docx"].includes(fileExt);

    if (!ACCEPTED_RESUME_TYPES.includes(file.type) && !hasValidExt) {
      setFieldError("Resume must be a PDF or Word document.");
      setResumeFile(null);
      e.target.value = "";
      return;
    }

    if (file.size > MAX_RESUME_MB * 1024 * 1024) {
      setFieldError(`Resume must be under ${MAX_RESUME_MB}MB.`);
      setResumeFile(null);
      e.target.value = "";
      return;
    }

    setFieldError("");
    setResumeFile(file);
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    setFieldError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (formData.website) {
      // Honeypot field — silently drop bot submissions without an error
      return;
    }

    if (!formData.position) {
      setFieldError("Please select a position.");
      return;
    }

    if (!formData.name.trim() || !formData.city.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setFieldError("Name, current city, email, and phone are required.");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email.trim())) {
      setFieldError("Please enter a valid email address.");
      return;
    }

    if (!phoneVerified || !verifiedPhone) {
      setFieldError("Please verify your mobile number with the OTP before submitting.");
      return;
    }

    if (!resumeFile) {
      setFieldError("Please attach your resume.");
      return;
    }

    setFieldError("");
    setSubmitting(true);

    let storageRef = null;

    try {
      const timestamp = Date.now();
      const safeFileName = resumeFile.name.replace(/[^\w.\-]+/g, "_");
      storageRef = ref(storage, `career_applications/${timestamp}_${safeFileName}`);

      await uploadBytes(storageRef, resumeFile);
      const resumeURL = await getDownloadURL(storageRef);

      await addDoc(collection(db, "career_applications"), {
        jobTitle: formData.position,
        name: formData.name.trim(),
        city: formData.city.trim(),
        email: formData.email.trim(),
        // The exact number the OTP confirmed, not whatever is in the input.
        phone: verifiedPhone,
        phoneVerified: true,
        message: formData.message.trim(),
        resumeURL,
        resumeName: resumeFile.name,
        status: "new",
        submittedAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Career application submission failed:", err);
      setSubmitError("Something went wrong while submitting. Please try again.");

      // Clean up the uploaded file if the Firestore write failed
      if (storageRef) {
        try {
          await deleteObject(storageRef);
        } catch (cleanupErr) {
          console.error("Failed to clean up orphaned resume file:", cleanupErr);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const handleReset = () => {
      setFormData(initialFormState);
      setResumeFile(null);
      setFieldError("");
      setSubmitError("");
      setSubmitted(false);
      resetPhoneVerification();
    };

    return (
      <div className="cr-apply-card cr-modal-success">
        <FaCheckCircle className="cr-modal-success-icon" />
        <h3 className="ab-vm-title">Application Submitted</h3>
        <p>
          Thanks for applying to <strong>{formData.position}</strong>. Our
          team will review your details and get back to you.
        </p>
        <button type="button" className="cr-btn" onClick={handleReset}>
          Apply for Another Role
        </button>
      </div>
    );
  }

  return (
    <div className="cr-apply-card">
      <form onSubmit={handleSubmit} className="cr-form">
        <div className="cr-form-group">
          <label htmlFor="position">Position</label>
          <select
            id="position"
            name="position"
            value={formData.position}
            onChange={handleChange}
            disabled={submitting}
            required
          >
            <option value="" disabled>
              Select a position
            </option>
            {positions.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </select>
        </div>

        <div className="cr-form-row">
          <div className="cr-form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              disabled={submitting}
              required
            />
          </div>

          <div className="cr-form-group">
            <label htmlFor="city">Current City</label>
            <input
              id="city"
              name="city"
              type="text"
              value={formData.city}
              onChange={handleChange}
              placeholder="e.g. New Delhi"
              disabled={submitting}
              required
            />
          </div>
        </div>

        <div className="cr-form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            disabled={submitting}
            required
          />
        </div>

        <div className="cr-form-group">
          <label htmlFor="phone">Mobile Number</label>
          <div className="cr-otp-row">
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="10-digit mobile number"
              disabled={submitting || otpBusy}
              required
            />
            {phoneVerified ? (
              <span className="cr-otp-verified">
                <FaCheckCircle /> Verified
              </span>
            ) : (
              <button
                type="button"
                className="cr-btn cr-otp-btn"
                onClick={handleSendOtp}
                disabled={submitting || otpBusy || !formData.phone.trim()}
              >
                {otpBusy && !otpSent ? "Sending..." : otpSent ? "Resend OTP" : "Verify"}
              </button>
            )}
          </div>

          {otpSent && !phoneVerified && (
            <div className="cr-otp-row cr-otp-confirm-row">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter the 6-digit OTP"
                disabled={otpBusy}
              />
              <button
                type="button"
                className="cr-btn cr-otp-btn"
                onClick={handleConfirmOtp}
                disabled={otpBusy || otpCode.length < 6}
              >
                {otpBusy ? "Checking..." : "Confirm"}
              </button>
            </div>
          )}

          {otpError && <p className="cr-form-error">{otpError}</p>}
          {/* Invisible reCAPTCHA anchor for Firebase phone auth */}
          <div id="cr-recaptcha" />
        </div>

        <div className="cr-form-group">
          <label htmlFor="message">Message (optional)</label>
          <textarea
            id="message"
            name="message"
            rows={3}
            value={formData.message}
            onChange={handleChange}
            placeholder="Anything you'd like us to know"
            disabled={submitting}
          />
        </div>

        <div className="cr-form-group">
          <label htmlFor="resume" className="cr-file-label">
            <div className="cr-file-left">
              <FaPaperclip />
              <span>
                {resumeFile
                  ? resumeFile.name
                  : "Attach resume (PDF or Word, max 5MB)"}
              </span>
            </div>

            {resumeFile && (
              <FaTimes
                className="cr-remove-icon"
                onClick={(e) => {
                  e.preventDefault();
                  handleRemoveResume();
                }}
              />
            )}
          </label>
          <input
            ref={fileInputRef}
            id="resume"
            name="resume"
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            disabled={submitting}
            className="cr-file-input"
          />
        </div>

        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          className="cr-file-input"
          aria-hidden="true"
        />

        {fieldError && <p className="cr-form-error">{fieldError}</p>}

        {submitError && <p className="cr-form-error">{submitError}</p>}

        <button type="submit" className="cr-btn cr-btn--block" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </form>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const CareerPage = () => {
  // Live job postings from Firestore (`career_jobs`), managed from the
  // company ERP. Only open roles are shown; if none exist yet the page
  // falls back to the static POSITIONS list in the form and simply hides
  // the Open Positions section.
  const [openJobs, setOpenJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, "career_jobs"), where("isOpen", "==", true))
        );
        const jobs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Newest first; createdAt can be missing on hand-made docs.
        jobs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setOpenJobs(jobs);
      } catch (err) {
        console.error("Failed to load job postings:", err);
      } finally {
        setJobsLoaded(true);
      }
    })();
  }, []);

  const formPositions =
    openJobs.length > 0 ? [...openJobs.map((j) => j.title), "Other"] : POSITIONS;

  const scrollToApply = () => {
    const el = document.getElementById("cr-apply");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const applyForJob = (title) => {
    setSelectedPosition(title);
    scrollToApply();
  };

  // Job whose full details are open in the Read More dialog (null = closed).
  const [detailJob, setDetailJob] = useState(null);

  return (
    <div className="ab-root cr-root">
      <div className="ab-page-content">

        {/* ══ HERO ═══════════════════════════════════════════════ */}
        <section className="ab-section">
          <div className="ab-section-inner">
            <div className="ab-hero-banner">
              <div className="ab-hero-text">
                <span className="ab-badge">JOIN OUR TEAM</span>
                <h1 className="ab-hero-title">
                  Build Your Career<br />
                  <span className="ab-hero-green">With BiBA Electronics.</span>
                </h1>
                <p className="ab-hero-desc">
                  We're looking for passionate engineers, developers, and business
                  professionals who want to solve real-world challenges across
                  <em> Embedded Systems</em>, <em>Software Development</em>,
                  <em> TFT Displays</em>, and
                  <em> Electronics Manufacturing</em>. Grow your career with a team
                  that values innovation, ownership, and continuous learning.
                </p>
              </div>

              <div className="ab-hero-badge-col">
                <div className="cr-hero-image-card">
                  <img src={careerImg} alt="BiBA Electronics team collaborating in the office" />
                </div>
                {/* <blockquote className="ab-quote">
                  "Innovation starts with people. Whether you build software, embedded systems, or business solutions, your ideas help shape the future at BiBA Electronics."
                </blockquote> */}
              </div>
            </div>
          </div>
        </section>

        {/* ══ OPEN POSITIONS ═══════════════════════════════════════ */}
        {jobsLoaded && openJobs.length > 0 && (
          <section className="ab-section" id="cr-openings">
            <div className="ab-section-inner">
              <div className="ab-section-head">
                <span className="ab-badge">WE'RE HIRING</span>
                <h2 className="ab-section-title">Open Positions</h2>
                <p className="ab-section-desc">
                  {openJobs.length} {openJobs.length === 1 ? "role" : "roles"} currently open — find the one that fits you.
                </p>
              </div>

              <div className="cr-job-grid">
                {openJobs.map((job, index) => (
                  <motion.div
                    className="ab-comp-card cr-job-card"
                    key={job.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: index * 0.06 }}
                  >
                    <h3 className="ab-comp-title">{job.title}</h3>

                    <div className="cr-job-meta">
                      {job.department && (
                        <span className="cr-job-tag">
                          <FaUsers /> {job.department}
                        </span>
                      )}
                      {job.location && (
                        <span className="cr-job-tag">
                          <FaMapMarkerAlt /> {job.location}
                        </span>
                      )}
                      {job.employmentType && (
                        <span className="cr-job-tag">
                          <FaBriefcase /> {job.employmentType}
                        </span>
                      )}
                    </div>

                    {job.description && <p className="ab-comp-desc cr-job-desc">{job.description}</p>}

                    <div className="cr-job-actions">
                      <button
                        type="button"
                        className="cr-btn cr-btn--ghost"
                        onClick={() => setDetailJob(job)}
                      >
                        Read More
                      </button>
                      <button
                        type="button"
                        className="cr-btn cr-job-apply"
                        onClick={() => applyForJob(job.title)}
                      >
                        Apply Now <FaArrowRight />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ══ JOB DETAILS DIALOG (Read More) ═══════════════════════ */}
        {detailJob && (
          <div className="cr-jobmodal-overlay" onClick={() => setDetailJob(null)}>
            <div
              className="cr-jobmodal"
              role="dialog"
              aria-modal="true"
              aria-label={detailJob.title}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cr-jobmodal-close"
                aria-label="Close"
                onClick={() => setDetailJob(null)}
              >
                <FaTimes />
              </button>

              <h3 className="ab-comp-title cr-jobmodal-title">{detailJob.title}</h3>

              <div className="cr-job-meta">
                {detailJob.department && (
                  <span className="cr-job-tag">
                    <FaUsers /> {detailJob.department}
                  </span>
                )}
                {detailJob.location && (
                  <span className="cr-job-tag">
                    <FaMapMarkerAlt /> {detailJob.location}
                  </span>
                )}
                {detailJob.employmentType && (
                  <span className="cr-job-tag">
                    <FaBriefcase /> {detailJob.employmentType}
                  </span>
                )}
              </div>

              <div className="cr-jobmodal-body">
                {!detailJob.description && !detailJob.candidateProfile && (
                  <p>Details for this role will be shared during the screening call.</p>
                )}
                {detailJob.description && <p className="cr-jobmodal-text">{detailJob.description}</p>}
                {detailJob.candidateProfile && (
                  <>
                    <h4 className="cr-jobmodal-heading">Candidate Profile</h4>
                    <p className="cr-jobmodal-text">{detailJob.candidateProfile}</p>
                  </>
                )}
              </div>

              <button
                type="button"
                className="cr-btn cr-btn--block"
                onClick={() => {
                  setDetailJob(null);
                  applyForJob(detailJob.title);
                }}
              >
                Apply Now <FaArrowRight />
              </button>
            </div>
          </div>
        )}

        {/* ══ HIRING PROCESS ═══════════════════════════════════════ */}
        <section className="ab-section">
          <div className="ab-section-inner">
            <div className="ab-section-head">
              <span className="ab-badge">HOW IT WORKS</span>
              <h2 className="ab-section-title">Hiring Process</h2>
            </div>

            <div className="cr-process-grid">
              {PROCESS_STEPS.map((step, index) => (
                <motion.div
                  className="ab-vm-card cr-process-card"
                  key={step.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <div className="ab-vm-icon-wrap cr-process-num">{index + 1}</div>
                  <div className="cr-process-icon">{step.icon}</div>
                  <h3 className="ab-vm-title cr-process-label">{step.label}</h3>
                  <p className="ab-vm-body cr-process-desc">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ WHY JOIN ═══════════════════════════════════════════ */}
        <section className="ab-section ab-section--alt">
          <div className="ab-section-inner">
            <div className="ab-section-head">
              <span className="ab-badge">WHY JOIN</span>
              <h2 className="ab-section-title">Why Build Your Career With Us?</h2>
              <p className="ab-section-desc">
                A few reasons engineers, software developers, and business professionals choose to grow at BiBA Electronics.
              </p>
            </div>

            <div className="ab-competency-grid cr-benefit-grid">
              {BENEFITS.map((item, index) => (
                // <motion.div
                //   className="ab-comp-card"
                //   key={item.title}
                //   initial={{ opacity: 0, y: 30 }}
                //   whileInView={{ opacity: 1, y: 0 }}
                //   viewport={{ once: true, amount: 0.3 }}
                //   transition={{ duration: 0.5, delay: index * 0.06 }}
                // >
                //   <div className="cr-benefit-icon-wrap">{item.icon}</div>
                //   <h3 className="ab-comp-title">{item.title}</h3>
                //   <p className="ab-comp-desc">{item.desc}</p>
                // </motion.div>
                <motion.div
                  className="ab-comp-card"
                  key={item.title}
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  whileHover={{
                    y: -10,
                    scale: 1.03,
                    transition: {
                      duration: 0.3,
                      ease: "easeOut",
                    },
                  }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.08,
                  }}
                >
                  <div className="cr-benefit-icon-wrap">
                    {item.icon}
                  </div>

                  <h3 className="ab-comp-title">
                    {item.title}
                  </h3>

                  <p className="ab-comp-desc">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ APPLY ══════════════════════════════════════════════ */}
        <section className="ab-section ab-section--alt" id="cr-apply">
          <div className="ab-section-inner ab-section-inner--narrow">
            <div className="ab-section-head">
              <span className="ab-badge">APPLY NOW</span>
              <h2 className="ab-section-title">Apply for a Role</h2>
              <p className="ab-section-desc">
                Pick the position you're interested in and share a few details — we'll take it from there.
              </p>
            </div>

            <ApplicationForm positions={formPositions} selectedPosition={selectedPosition} />
          </div>
        </section>

      </div>
    </div>
  );
};

export default CareerPage;